/**
 * Shrink an image in the browser before it is uploaded.
 *
 * Why: profile photos were stored exactly as they came off the phone. A single
 * iPhone shot (IMG_2668.jpeg, 2.7 MB, ~3000 px) was re-downloaded by every
 * viewer on every page load just to be painted into a 96 px circle. Firebase
 * Storage also serves user uploads as `private, max-age=0`, so the browser
 * cannot keep it — one measured page load spent 2.9 s receiving it, another
 * 1.1 s on a 304 that returned nothing.
 *
 * Re-encoding to at most AVATAR_MAX_DIMENSION px turns those megabytes into a
 * few dozen kilobytes, and uploading with an explicit `cacheControl` (see
 * AVATAR_CACHE_CONTROL) means the second view costs nothing at all.
 *
 * Deliberate non-goals / safety rails:
 *   - Animated GIFs and SVGs are returned untouched; canvas re-encoding would
 *     flatten an animation to one frame and rasterize a vector.
 *   - If the re-encode does not actually come out smaller (already-optimised
 *     small avatars), the original is kept.
 *   - Decoding goes through an <img> element rather than createImageBitmap.
 *     That is on purpose: <img> applies EXIF orientation per spec, so photos
 *     taken in portrait stay upright. createImageBitmap needs an
 *     `imageOrientation` option that older browsers silently ignore, which
 *     would land sideways avatars in production.
 */

// 96 px is the largest place an avatar is shown (.us-avatar-img); 512 covers
// that at 3× DPR with room to spare and still lands well under 100 KB.
export const AVATAR_MAX_DIMENSION = 512;
export const AVATAR_QUALITY = 0.82;

// Content-addressed filenames (see buildAvatarPath) make the bytes immutable,
// so they can be cached hard — a new photo is a new URL.
export const AVATAR_CACHE_CONTROL = 'public, max-age=31536000, immutable';

/** Types we must not run through a canvas. */
const PASS_THROUGH_TYPES = ['image/gif', 'image/svg+xml'];

/**
 * Whether this file should be uploaded exactly as-is.
 *
 * @param {File|Blob} file the picked file
 * @returns {boolean} true when re-encoding would be wrong or pointless
 */
export const shouldPassThrough = (file) => {
  if (!file || !file.type) return true;
  if (!file.type.startsWith('image/')) return true;
  return PASS_THROUGH_TYPES.includes(file.type);
};

/**
 * Scales dimensions down to fit a square bound, preserving aspect ratio.
 * Never scales up — a 200 px photo stays 200 px.
 *
 * @param {number} width source width in px
 * @param {number} height source height in px
 * @param {number} max longest allowed edge in px
 * @returns {{ width: number, height: number }} integer target size (min 1 px)
 */
export const fitWithin = (width, height, max) => {
  if (!width || !height) return { width: 0, height: 0 };
  const longest = Math.max(width, height);
  if (longest <= max) return { width, height };
  const scale = max / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

/**
 * Builds the storage path for an avatar.
 *
 * The old code used the raw `file.name`, which meant re-uploading a photo with
 * a different name silently orphaned the previous one AND kept the extension of
 * the source even though we now change the format. A timestamp instead makes
 * every upload its own immutable URL, so the long cache header above is safe
 * and a changed photo shows up instantly.
 *
 * @param {string} userId owner uid
 * @param {string} extension file extension without the dot
 * @param {number} [now] epoch millis, injectable for tests
 * @returns {string} storage path
 */
export const buildAvatarPath = (userId, extension, now = Date.now()) =>
  `users/${userId}/userPhoto/avatar_${now}.${extension}`;

/** Maps an output mime type to the extension we store it under. */
export const extensionFor = (mimeType) => (mimeType === 'image/webp' ? 'webp' : 'jpg');

let webpSupport = null;

/**
 * Whether this browser can ENCODE webp from a canvas (reading it is more
 * widely supported than writing it, so feature-detect the write).
 *
 * @returns {boolean} true when canvas.toDataURL yields real webp
 */
export const canEncodeWebp = () => {
  if (webpSupport !== null) return webpSupport;
  try {
    const probe = document.createElement('canvas');
    probe.width = 1;
    probe.height = 1;
    webpSupport = probe.toDataURL('image/webp').startsWith('data:image/webp');
  } catch (e) {
    webpSupport = false;
  }
  return webpSupport;
};

/** Loads a File into an <img>, which applies EXIF orientation for us. */
const loadImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => resolve({ img, revoke: () => URL.revokeObjectURL(url) });
  img.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error('Image could not be decoded'));
  };
  img.src = url;
});

const canvasToBlob = (canvas, type, quality) => new Promise((resolve) => {
  canvas.toBlob((blob) => resolve(blob), type, quality);
});

/**
 * Re-encodes an image down to a bounded size.
 *
 * Always resolves — on any failure it hands back the original file so a picky
 * image can never block someone from setting their profile photo.
 *
 * @param {File} file the picked file
 * @param {object} [options] overrides
 * @param {number} [options.maxDimension] longest edge of the result
 * @param {number} [options.quality] encoder quality, 0..1
 * @returns {Promise<{ blob: Blob, contentType: string, extension: string,
 *   width: number, height: number, originalBytes: number, bytes: number,
 *   compressed: boolean }>} the upload payload
 */
export async function compressImage(file, options = {}) {
  const maxDimension = options.maxDimension || AVATAR_MAX_DIMENSION;
  const quality = options.quality || AVATAR_QUALITY;

  const keepOriginal = (reason) => ({
    blob: file,
    contentType: file.type || 'application/octet-stream',
    extension: (file.name || '').split('.').pop() || 'jpg',
    width: 0,
    height: 0,
    originalBytes: file.size,
    bytes: file.size,
    compressed: false,
    reason,
  });

  if (shouldPassThrough(file)) return keepOriginal('unsupported-type');

  let loaded;
  try {
    loaded = await loadImage(file);
  } catch (e) {
    console.warn('[compressImage] decode failed, uploading original:', e.message);
    return keepOriginal('decode-failed');
  }

  try {
    const { img } = loaded;
    const target = fitWithin(img.naturalWidth, img.naturalHeight, maxDimension);
    if (!target.width || !target.height) return keepOriginal('no-dimensions');

    const useWebp = canEncodeWebp();
    const contentType = useWebp ? 'image/webp' : 'image/jpeg';

    const canvas = document.createElement('canvas');
    canvas.width = target.width;
    canvas.height = target.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return keepOriginal('no-canvas-context');

    // JPEG has no alpha: without this, a transparent PNG avatar would come out
    // on a black background. WebP keeps the alpha channel, so leave it clear.
    if (!useWebp) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, target.width, target.height);
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, target.width, target.height);

    const blob = await canvasToBlob(canvas, contentType, quality);
    if (!blob) return keepOriginal('encode-failed');
    // An already-small, well-optimised avatar can encode larger than it came.
    if (blob.size >= file.size) return keepOriginal('no-gain');

    return {
      blob,
      contentType,
      extension: extensionFor(contentType),
      width: target.width,
      height: target.height,
      originalBytes: file.size,
      bytes: blob.size,
      compressed: true,
    };
  } catch (e) {
    console.warn('[compressImage] re-encode failed, uploading original:', e.message);
    return keepOriginal('exception');
  } finally {
    loaded.revoke();
  }
}

export default compressImage;
